import { AgGridReact } from 'ag-grid-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import DefaultButton from '../../../../components/Buttons/Default';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import { printUserStatus } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/settings/users/edit/${data.id}`, { state: { userData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      {data?.master_id !== null && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.setUserToDelete(data.id);
          }}
          size="sm" outline color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function UsersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const getUsers = useCallback(async () => {
    setLoading(true);
    const data = await get('/settings/users?page=' + currentPage + '&page_size=' + pageSize);
    if (data?.users?.data) {
      setRowData(data.users.data);
      setTotalPages(data.users.last_page);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const deleteUser = async () => {
    const data = await del('/settings/users/delete/' + userToDelete);
    if (data?.success) {
      toast.success(data.message || t('settings.userDeleted'));
      getUsers();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setUserToDelete(null);
  };

  const columnDefs = useMemo(() => [
    {
      field: 'firstname',
      headerName: t('common.name'),
      flex: 1,
      sortable: true,
      valueFormatter: ({ data }) => `${data.firstname} ${data.lastname}`
    },
    { field: 'email', headerName: t('common.email'), flex: 1, sortable: true },
    { field: 'phone_number', headerName: t('common.phone'), width: 150, sortable: true },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 120, sortable: true,
      cellRenderer: ({ value }) => printUserStatus(value)
    },
    {
      field: 'role',
      headerName: t('settings.role'),
      width: 150,
      sortable: true,
      valueFormatter: ({ data }) => {
        let role = '';
        if(data.roles?.length > 0) role = data.roles[0].name;
        return role.charAt(0).toUpperCase() + role.slice(1);
      }
    },
    { headerName: t('common.options'), width: 110, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setUserToDelete }), [navigate]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('settings.users')}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/settings/users/create')}>
            {t('settings.newUser')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={userToDelete !== null}
        title={t('settings.deleteUser')}
        message={t('settings.deleteUserConfirm')}
        onConfirm={deleteUser}
        toggle={() => setUserToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default UsersPage;
